import { useCurrentUserContext } from '@/modules/auth/hooks/useCurrentUserContext';
import { Modal } from '@/modules/common/components/Modal';
import { MoneyInput } from '@/modules/common/components/MoneyInput';
import {
  integerToMoney,
  moneyToInteger,
} from '@/modules/common/lib/moneyHandler';

import { useGetEmployees } from '@/modules/employees/hooks/useEmployees';
import { IEmployeeOverrideWithEmployee } from '@/types/learning-budget';
import { ITeam } from '@/types/team';
import { IUserWithTeam } from '@/types/user';
import { yupResolver } from '@hookform/resolvers/yup';
import { CheckCircle, InfoOutlined } from '@mui/icons-material';
import {
  Alert,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  List,
  ListItem,
  ListItemText,
  ModalProps,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import * as yup from 'yup';

export type FormValues = {
  employees: (Omit<IEmployeeOverrideWithEmployee['employee'], 'team'> & {
    team?: Partial<Pick<ITeam, '_id' | 'name'>>;
    selected: boolean;
  })[];
  budget: IEmployeeOverrideWithEmployee['budget'];
};

export type EmployeeOutput = Omit<
  FormValues['employees'][number],
  'team' | 'selected'
> & {
  team?: string;
};

export type SubmissionValues = {
  employees: EmployeeOutput[];
  budget: FormValues['budget'];
};

type LearningBudgetSetMembersModalProps = Omit<
  ModalProps,
  'onClose' | 'title' | 'subTitle' | 'children'
> & {
  open: boolean;
  onClose: (data?: SubmissionValues) => void;
};

export function LearningBudgetSetMembersModal({
  onClose,
  ...props
}: LearningBudgetSetMembersModalProps) {
  const userContext = useCurrentUserContext();
  const employerId = userContext?.employer?._id;

  const [search, setSearch] = useState('');

  const {
    data: { data = [] } = {},
    isLoading: isLoadingEmployees,
    isRefetching: isRefetchingEmployees,
  } = useGetEmployees(
    employerId ?? '',
    search ? { name: search } : {},
    { limit: 10 },
    { enabled: !!employerId },
  );

  const employeesSearchResult = data as IUserWithTeam[];

  const isLoading = isLoadingEmployees || isRefetchingEmployees;

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
  } = useForm<FormValues>({
    defaultValues: {
      employees: [],
      budget: {
        amount: 0,
        currency: 'EUR',
      },
    },
    resolver: yupResolver(
      yup.object().shape({
        employees: yup
          .array()
          .of(
            yup
              .object()
              .shape({
                _id: yup.string().required(),
                firstName: yup.string(),
                lastName: yup.string(),
                team: yup
                  .object()
                  .shape({
                    _id: yup.string().uuid(),
                    name: yup.string(),
                  })
                  .optional(),
                selected: yup.boolean().required(),
              })
              .required(),
          )
          .required()
          .min(1, 'At least one employee is required')
          .test(
            'employees-selected',
            'At least one employee should be selected',
            value => value.some(employee => employee.selected),
          ),
        budget: yup.object().shape({
          amount: yup
            .number()
            .required('Amount is required')
            .typeError('Amount is required')
            .positive('Amount must be positive'),
          currency: yup.string().required().oneOf(['EUR']),
        }),
      }),
    ),
  });

  const {
    fields: employees,
    append,
    update,
  } = useFieldArray({
    control,
    name: 'employees',
  });

  const onSubmitForm = (data: FormValues) => {
    const selectedEmployees = data.employees
      .filter(employee => employee.selected)
      .map(employee => ({
        _id: employee._id,
        firstName: employee.firstName,
        lastName: employee.lastName,
        team: employee.team?._id,
      }));

    onClose?.({
      employees: selectedEmployees,
      budget: data.budget,
    });
  };

  return (
    <Modal
      {...props}
      onClose={() => {
        onClose();
        reset();
      }}
      title="Set Learning Budget for Members"
      subTitle="Allocate learning budgets per member for this cycle."
      actions={
        <>
          <Button
            variant="outlined"
            onClick={() => onClose()}
            disabled={isLoading || isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            form="learning-budget-set-members-modal-form"
            disabled={isLoading}
            loading={isSubmitting}
          >
            Confirm
          </Button>
        </>
      }
      classes={{ paper: 'max-w-2xl' }}
    >
      <form
        id="learning-budget-set-members-modal-form"
        onSubmit={handleSubmit(onSubmitForm)}
      >
        <Stack gap={3}>
          <Autocomplete
            options={employeesSearchResult}
            getOptionLabel={option =>
              [option.firstName, option.lastName, option.email]
                .filter(Boolean)
                .join(' ') || ''
            }
            loading={isLoading}
            inputValue={search}
            onInputChange={(_, value) => setSearch(value)}
            handleHomeEndKeys
            selectOnFocus
            clearOnBlur
            onChange={(_, value) => {
              if (
                value?._id &&
                !employees.some(employee => employee._id === value._id)
              ) {
                append({
                  _id: value._id,
                  firstName: value.firstName,
                  lastName: value.lastName,
                  team: value.team,
                  selected: true,
                });
              }
            }}
            renderInput={params => (
              <TextField {...params} size="small" className="my-[2px]" />
            )}
            slotProps={{
              popper: {
                className: 'shadow-md border-none',
              },
              listbox: {
                className: 'py-0',
              },
            }}
            renderValue={value => {
              return (
                <Typography className="mx-2">
                  <span className="font-medium">
                    {[value.firstName, value.lastName]
                      .filter(Boolean)
                      .join(' ')}{' '}
                  </span>
                  <span className="text-secondary">&lt;{value.email}&gt;</span>
                </Typography>
              );
            }}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              return (
                <li
                  {...optionProps}
                  key={key}
                  className={`flex flex-col px-3 py-2 items-start cursor-pointer ${
                    optionProps.className ?? ''
                  }`}
                >
                  <span className="font-semibold">
                    {[option.firstName, option.lastName]
                      .filter(Boolean)
                      .join(' ')}
                  </span>

                  <span className="text-xs text-gray-500">
                    &lt;{option.email}&gt;
                  </span>
                </li>
              );
            }}
          />
          <FormControl
            className="flex flex-col gap-1"
            error={!!errors.employees}
          >
            <List
              className={`rounded-xl border p-0 min-h-12 overflow-auto max-h-[250px] ${
                errors.employees ? 'border-error' : ''
              }`}
            >
              {employees.length > 0 ? (
                employees.map((employee, idx) => (
                  <ListItem
                    key={employee._id}
                    divider={idx !== employees.length - 1}
                    secondaryAction={
                      <Checkbox
                        edge="end"
                        checked={employee.selected}
                        tabIndex={-1}
                        disableRipple
                        icon={
                          <CheckCircle className="text-primary opacity-20" />
                        }
                        checkedIcon={<CheckCircle className="text-primary" />}
                        onChange={() => {
                          update(idx, {
                            ...employee,
                            selected: !employee.selected,
                          });
                        }}
                      />
                    }
                    className={`py-2 px-4 ${employee.selected ? 'bg-light-gray' : ''}`}
                  >
                    <ListItemText
                      className="m-0"
                      primary={
                        <Typography fontWeight={600}>
                          {employee.firstName} {employee.lastName}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          {employee.team?.name ? (
                            <>
                              <span className="capitalize">
                                {employee.team.name}
                              </span>{' '}
                              team
                            </>
                          ) : (
                            'No team selected'
                          )}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText>
                    <span className="text-secondary">
                      No employees selected
                    </span>
                  </ListItemText>
                </ListItem>
              )}
            </List>
            <FormHelperText error={!!errors.employees}>
              {errors.employees?.message}
            </FormHelperText>
          </FormControl>

          <FormControl className="flex flex-col gap-1">
            <Typography
              variant="subtitle2"
              component="label"
              htmlFor="budget.amount"
            >
              Enter Learning Budget Amount
            </Typography>

            <Controller
              control={control}
              name="budget.amount"
              render={({
                field: { value, onChange, ref: inputRef, ...field },
              }) => {
                return (
                  <MoneyInput
                    error={!!errors.budget?.amount}
                    value={integerToMoney(value)}
                    onChange={e => {
                      onChange(moneyToInteger(e.target.value));
                    }}
                    {...field}
                    inputRef={inputRef}
                    id="budget.amount"
                    decimalScale={2}
                    startAdornment={
                      <Typography
                        className={`text-2xl font-semibold ${
                          isLoading || isSubmitting ? 'text-gray-400' : ''
                        }`}
                      >
                        €
                      </Typography>
                    }
                    allowNegative={true}
                    classes={{
                      root: 'flex-1 rounded-none text-center',
                      input: 'text-center text-2xl font-semibold',
                      focused: 'outline-none',
                    }}
                  />
                );
              }}
            />
            {errors.budget?.amount && (
              <FormHelperText error={!!errors.budget?.amount}>
                {errors.budget?.amount?.message}
              </FormHelperText>
            )}
          </FormControl>

          <Alert
            severity="info"
            variant="filled"
            className="bg-info-light text-info-dark rounded-lg"
            icon={<InfoOutlined />}
          >
            To add members, go to the Employees page or click the 'Manage
            Employees' button. To assign a team, open the Employees page, select
            the Teams tab, or click the 'Manage Teams' button.
          </Alert>
        </Stack>
      </form>
    </Modal>
  );
}
